/*global MCCordovaPlugin */
import { useCallback, useMemo } from 'react';

import { Capacitor } from '@capacitor/core';
import { useLocation } from 'react-router-dom';

import { useFlag } from 'state/launchdarkly';
import { CustomEventNames } from 'state/mParticle';
import { deviceInfo, isNative } from 'utils/environment';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

interface ISalesForceResult {
  handleLogPageView: () => void;
  handleDispatchEvent: (eventName: string) => void;
}

export const useSalesForceInApp = (): ISalesForceResult => {
  const location = useLocation();
  const isNativeDevice = useMemo(() => deviceInfo() !== 'web', []);

  const enableSalesForceInAppNotifications = useFlag(
    LaunchDarklyFlag.ENABLE_SALESFORCE_IN_APP_NOTIFICATIONS
  );

  const isInAppEnabled = useMemo(
    () => enableSalesForceInAppNotifications && isNativeDevice && isNative && Capacitor.isNative,
    [enableSalesForceInAppNotifications, isNativeDevice]
  );
  const handleLogPageView = useCallback(() => {
    if (!isInAppEnabled) {
      return;
    }

    if (location.pathname) {
      MCCordovaPlugin.track(CustomEventNames.PAGE_VIEW, { screenName: location.pathname });
    }
  }, [isInAppEnabled, location.pathname]);

  const handleDispatchEvent = useCallback(
    (eventName: string) => {
      if (!isInAppEnabled) {
        return;
      }

      MCCordovaPlugin.track(eventName, { eventDispatched: eventName });
    },
    [isInAppEnabled]
  );

  return {
    handleLogPageView,
    handleDispatchEvent,
  };
};
